import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { useFileStoreData } from "../../Hooks/useFileStoreData";
import { publicResourceUrl } from "../../config/publicResources";

const GOVT_EMBLEM = "/assets/GovtOfBiharEmblem.jpg";
const KRISHI_LOGO = "/assets/RewampBiharKrishiLogo.png";

const CONTACTS_URL = publicResourceUrl(
  "publicresources/json/AssistedMode/contacts.json"
);

const resolveLang = (raw) =>
  String(raw || "").toLowerCase().startsWith("hi") ? "hi" : "en";

const localized = (field, lang) => field?.[lang] ?? field?.en ?? "";

export const RewampMasthead = React.memo(() => {
  const { t, i18n } = useTranslation();
  const lang = resolveLang(i18n.language);

  const { data } = useFileStoreData({ url: CONTACTS_URL, key: "contacts" });
  const officials = (data?.CmAgriMinister || []).slice(0, 2);

  return (
    <Box
      component="div"
      sx={{
        bgcolor: "background.paper",
        borderBottom: "0.8px solid",
        borderColor: "divider",
        px: { xs: 2, md: 4, lg: 9 },
        py: { xs: 1.5, md: 1 },
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: { xs: 2, md: 3 },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1.5, md: 2 } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL}${GOVT_EMBLEM}`}
            alt={t("GOVT_OF_BIHAR_EMBLEM")}
            title={t("GOVT_OF_BIHAR_EMBLEM")}
            // "Hide Images" a11y: hide as a box-fit placeholder at the logo's own
            // size (not the 240px content-image chip) — full label shown on hover.
            data-a11y-fit="box"
            sx={{
              width: { xs: 47, md: 71 },
              height: { xs: 40, md: 60 },
              objectFit: "contain",
              flexShrink: 0,
            }}
          />
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL}${KRISHI_LOGO}`}
            alt={t("BIHAR_KRISHI_LOGO")}
            title={t("BIHAR_KRISHI_LOGO")}
            data-a11y-fit="box"
            sx={{
              width: { xs: 56, md: 83.33 },
              height: { xs: 40, md: 60 },
              objectFit: "contain",
              flexShrink: 0,
            }}
          />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
          <Typography
            component="p"
            sx={(theme) => ({
              // Brand green on light; lightened accent green on dark (the dark
              // surface fails contrast against primary.dark). Light mode unchanged.
              color:
                theme.palette.mode === "dark"
                  ? theme.palette.text.textGreen
                  : theme.palette.primary.dark,
              fontSize: { xs: "1rem", md: "1.25rem" },
              fontWeight: 600,
              lineHeight: 1.5,
            })}
          >
            {t("DepartofAgri")}
          </Typography>
          <Typography
            component="p"
            sx={{
              color: "text.textGrey",
              fontSize: { xs: "0.8125rem", md: "0.875rem" },
              fontWeight: 500,
              lineHeight: 1.4,
            }}
          >
            {t("govtOfBihar")}
          </Typography>
        </Box>
      </Box>

      {officials.length > 0 && (
        <Box
          sx={{
            display: { xs: "none", lg: "flex" },
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 3,
          }}
        >
          {officials.map((official, idx) => {
            const name = localized(official?.name, lang);
            const position = localized(official?.position, lang);
            const isLeftSide = idx === 0;
            // Chief Minister (idx 0) to Agri Minister (idx 1) sized 20:18.
            const frameSize = isLeftSide ? 60 : 54;

            return (
              <React.Fragment key={official?.phone || official?.imageUrl || idx}>
                {idx === 1 && (
                  <Box
                    aria-hidden="true"
                    sx={{
                      width: "1px",
                      height: "50px",
                      bgcolor: "divider",
                      flexShrink: 0,
                    }}
                  />
                )}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    flexDirection: isLeftSide ? "row" : "row-reverse",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: isLeftSide ? "flex-end" : "flex-start",
                      maxWidth: 170,
                      px: "4px",
                    }}
                  >
                    <Typography
                      component="p"
                      sx={(theme) => ({
                        // On the dark masthead surface, green name text fails
                        // contrast — use light headings in dark mode.
                        color:
                          theme.palette.mode === "dark"
                            ? theme.palette.neutral.headings
                            : theme.palette.primary.dark,
                        fontWeight: 700,
                        fontSize: "0.875rem",
                        lineHeight: 1.35,
                        textAlign: isLeftSide ? "right" : "left",
                        whiteSpace: "nowrap",
                      })}
                    >
                      {name}
                    </Typography>
                    <Typography
                      component="p"
                      sx={{
                        color: "text.textGrey",
                        fontWeight: 400,
                        fontSize: "0.75rem",
                        lineHeight: 1.4,
                        textAlign: isLeftSide ? "right" : "left",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {position}
                    </Typography>
                  </Box>
                  <Box
                    sx={(theme) => ({
                      width: frameSize,
                      height: frameSize,
                      p: "1.6px",
                      border: `1px solid ${theme.palette.primary.dark}`,
                      borderRadius: "4px",
                      background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, #D8F3DC 100%)`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                      flexShrink: 0,
                    })}
                  >
                    <Box
                      component="img"
                      src={official?.imageUrl}
                      alt={[name, position].filter(Boolean).join(", ")}
                      title={[name, position].filter(Boolean).join(", ")}
                      data-a11y-fit="box"
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: "2px",
                      }}
                    />
                  </Box>
                </Box>
              </React.Fragment>
            );
          })}
        </Box>
      )}
    </Box>
  );
});

export default RewampMasthead;
